import { useState, useEffect, type Dispatch, type SetStateAction } from "react";

/**
 * Drop-in replacement for `useState` that persists to localStorage.
 *
 * - Reads from localStorage on mount (lazy initializer)
 * - Writes to localStorage on every state change
 * - JSON serialize/deserialize handles strings, numbers, booleans, arrays, objects
 * - Falls back to defaultValue if nothing stored or parse fails
 *
 * @example
 *   const [tab, setTab] = usePersistedState("altara_dm_activeTab", "setter-performance");
 */
export function usePersistedState<T>(
  key: string,
  defaultValue: T
): [T, Dispatch<SetStateAction<T>>] {
  const [state, setState] = useState<T>(() => {
    try {
      const saved = localStorage.getItem(key);
      if (saved === null) return defaultValue;
      return JSON.parse(saved) as T;
    } catch {
      return defaultValue;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(state));
    } catch {
      // localStorage full or unavailable — silently ignore
    }
  }, [key, state]);

  return [state, setState];
}
